import { supabase } from "@/lib/supabase-client";
import { ClientStatusType, ClientType } from "@/types/db/clients";
import { FileMetadata } from "@/types/files";

export const clientService = {
  async getClients() {
    const { data, error } = await supabase
      .from("clients")
      .select("id, description, name, status, meta_data, projects(count), created_at")
      // .select( "id, name, active, projects(count), estimates: projects(estimates(count)),invoices: projects(invoices(count)), created_at",)
      .order("created_at", { ascending: false });
    if (error) {
      throw Error(error.message);
    }

    // Extract markets from meta_data for table display
    const transformed = data?.map((client: any) => ({
      ...client,
      markets: (client.meta_data as any)?.markets || [],
    }));

    return transformed;
  },

  async getClientsByStatus(status: ClientStatusType): Promise<ClientType[]> {
    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .eq("status", status)
      .order("created_at", { ascending: false });
    if (error) {
      throw Error(error.message);
    }
    return data as ClientType[];
  },
  async getClientById(id: string): Promise<ClientType> {
    const { data, error } = await supabase.rpc("get_client", { p_client_id: id });
    if (error) throw Error(error.message);
    return data as ClientType;
  },

  async addClient(client: Partial<ClientType> & { tenant_id: string }): Promise<ClientType> {
    const { data, error } = await supabase.from("clients").insert([client]).select().single();

    if (error) {
      throw Error(error.message);
    }
    return data as ClientType;
  },

  async updateClient(id: string, patch: Partial<ClientType>): Promise<ClientType> {
    const { data, error } = await supabase.from("clients").update(patch).eq("id", id).select().single();
    if (error) {
      throw new Error(error.message);
    }
    return data as ClientType;
  },

  async deleteClient(id: string): Promise<boolean> {
    const { error } = await supabase.from("clients").delete().eq("id", id);
    if (error) {
      throw Error(error.message);
    }
    return true;
  },

  async deleteClientsByIds(ids: string[]): Promise<boolean> {
    const { error } = await supabase.from("clients").delete().in("id", ids);
    if (error) {
      throw Error(error.message);
    }
    return true;
  },

  async saveRowPatches(patches: (Partial<ClientType> & { id: string; markets?: string[] })[]): Promise<ClientType[]> {
    // Handle virtual fields - markets is stored in meta_data.markets
    const finalPatches = patches.map((patch) => {
      const { markets, ...rest } = patch;
      return rest;
    });

    const results = await Promise.all(
      finalPatches.map(async ({ id, ...patch }) => {
        const { data, error } = await supabase.from("clients").update(patch).eq("id", id).select().single();
        if (error) throw Error(error.message);
        return data;
      }),
    );

    return results as ClientType[];
  },

  // File management methods
  async uploadFile(clientId: string, file: File, uploadedBy: string): Promise<FileMetadata> {
    // Get tenant_id from client record
    const { data: tenantData, error: tenantError } = await supabase
      .from("clients")
      .select("tenant_id")
      .eq("id", clientId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get client tenant: ${tenantError?.message || "Client not found"}`);
    }

    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const filePath = `${tenantData.tenant_id}/${clientId}/${fileId}-${file.name}`;

    // Upload file to storage
    const { error: uploadError } = await supabase.storage.from("clients").upload(filePath, file);

    if (uploadError) {
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    // Create file metadata (store path, not URL)
    const fileMetadata: FileMetadata = {
      id: fileId,
      name: file.name,
      size: file.size,
      type: file.type,
      url: filePath, // Store path for signed URL generation
      bucket: "clients", // Store bucket name
      uploadedAt: new Date().toISOString(),
      uploadedBy: uploadedBy,
    };

    // No database metadata tracking needed - files are listed directly from storage
    return fileMetadata;
  },

  async getFiles(clientId: string): Promise<FileMetadata[]> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("clients")
      .select("tenant_id")
      .eq("id", clientId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get client tenant: ${tenantError?.message || "Client not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${clientId}`;
    const { data: files, error: listError } = await supabase.storage.from("clients").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list client files: ${listError.message}`);
    }

    if (!files || files.length === 0) {
      return [];
    }

    const fileMetadata: FileMetadata[] = files.map((file) => {
      const dashIndex = file.name.indexOf("-");
      const fileId = dashIndex > 0 ? file.name.substring(0, dashIndex) : file.name;
      const fileName = dashIndex > 0 ? file.name.substring(dashIndex + 1) : file.name;

      return {
        id: fileId,
        name: fileName,
        size: file.metadata?.size || 0,
        type: file.metadata?.mimetype || "application/octet-stream",
        url: `${storagePath}/${file.name}`, // Store path for signed URL generation
        bucket: "clients", // Store bucket name
        uploadedAt: file.created_at || new Date().toISOString(),
        uploadedBy: "unknown",
      };
    });

    return fileMetadata;
  },

  async deleteFile(clientId: string, fileId: string): Promise<boolean> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("clients")
      .select("tenant_id")
      .eq("id", clientId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get client tenant: ${tenantError?.message || "Client not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${clientId}`;
    const { data: files, error: listError } = await supabase.storage.from("clients").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list client files: ${listError.message}`);
    }

    const fileToDelete = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToDelete) {
      throw new Error("File not found in storage");
    }

    const filePath = `${storagePath}/${fileToDelete.name}`;
    const { error: storageError } = await supabase.storage.from("clients").remove([filePath]);

    if (storageError) {
      throw new Error(`Failed to delete from storage: ${storageError.message}`);
    }

    return true;
  },

  async renameFile(clientId: string, fileId: string, newName: string): Promise<FileMetadata> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("clients")
      .select("tenant_id")
      .eq("id", clientId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get client tenant: ${tenantError?.message || "Client not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${clientId}`;
    const { data: files, error: listError } = await supabase.storage.from("clients").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list client files: ${listError.message}`);
    }

    const fileToRename = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToRename) {
      throw new Error("File not found in storage");
    }

    const oldPath = `${storagePath}/${fileToRename.name}`;
    const newPath = `${storagePath}/${fileId}-${newName}`;

    const { error: moveError } = await supabase.storage.from("clients").move(oldPath, newPath);

    if (moveError) {
      throw new Error(`Failed to rename file: ${moveError.message}`);
    }

    return {
      id: fileId,
      name: newName,
      size: fileToRename.metadata?.size || 0,
      type: fileToRename.metadata?.mimetype || "application/octet-stream",
      url: newPath,
      bucket: "clients",
      uploadedAt: fileToRename.created_at || new Date().toISOString(),
      uploadedBy: "unknown",
    };
  },

  // Logo management methods
  async uploadLogo(clientId: string, file: File): Promise<string> {
    // Get tenant_id from client record
    const { data: clientData, error: clientError } = await supabase
      .from("clients")
      .select("tenant_id")
      .eq("id", clientId)
      .single();

    if (clientError || !clientData) {
      throw new Error(`Failed to get client tenant: ${clientError?.message || "Client not found"}`);
    }

    // Get file extension
    const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const filePath = `${clientData.tenant_id}/clients/${clientId}.${extension}`;

    // Delete any existing logo first
    try {
      await this.deleteLogo(clientId);
    } catch (error) {
      // Ignore error if no existing logo
      console.log("No existing logo to delete or delete failed:", error);
    }

    // Upload new logo to storage
    const { error: uploadError } = await supabase.storage.from("avatars").upload(filePath, file, {
      upsert: true,
    });

    if (uploadError) {
      throw new Error(`Logo upload failed: ${uploadError.message}`);
    }

    // Get public URL
    const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(filePath);

    return urlData.publicUrl;
  },

  async deleteLogo(clientId: string): Promise<boolean> {
    // Get tenant_id from client record
    const { data: clientData, error: clientError } = await supabase
      .from("clients")
      .select("tenant_id")
      .eq("id", clientId)
      .single();

    if (clientError || !clientData) {
      throw new Error(`Failed to get client tenant: ${clientError?.message || "Client not found"}`);
    }

    // Try to delete all possible logo file extensions
    const extensions = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
    const filePaths = extensions.map((ext) => `${clientData.tenant_id}/clients/${clientId}.${ext}`);

    // Also check for files without extension
    filePaths.push(`${clientData.tenant_id}/clients/${clientId}`);

    const { error } = await supabase.storage.from("avatars").remove(filePaths);

    if (error) {
      throw new Error(`Failed to delete logo: ${error.message}`);
    }

    return true;
  },

  // Link table management methods
  async syncBannedUsers(clientId: string, bannedUsers: { user: { id: string }; reason?: string }[]): Promise<void> {
    // Delete all existing banned users for this client
    const { error: deleteError } = await supabase
      .schema("link")
      .from("clients_banned_users")
      .delete()
      .eq("id", clientId);

    if (deleteError) {
      throw new Error(`Failed to delete existing banned users: ${deleteError.message}`);
    }

    // Insert new banned users
    if (bannedUsers.length > 0) {
      const records = bannedUsers.map((bu) => ({
        id: clientId, // client_id
        user_id: bu.user.id,
        reason: bu.reason || "",
      }));

      const { error: insertError } = await supabase.schema("link").from("clients_banned_users").insert(records);

      if (insertError) {
        throw new Error(`Failed to insert banned users: ${insertError.message}`);
      }
    }
  },

  async syncPreferredUsers(
    clientId: string,
    preferredUsers: { user: { id: string }; reason?: string }[],
  ): Promise<void> {
    // Delete all existing preferred users for this client
    const { error: deleteError } = await supabase
      .schema("link")
      .from("clients_preferred_users")
      .delete()
      .eq("id", clientId);

    if (deleteError) {
      throw new Error(`Failed to delete existing preferred users: ${deleteError.message}`);
    }

    // Insert new preferred users
    if (preferredUsers.length > 0) {
      const records = preferredUsers.map((pu) => ({
        id: clientId, // client_id
        user_id: pu.user.id,
        reason: pu.reason || "",
      }));

      const { error: insertError } = await supabase.schema("link").from("clients_preferred_users").insert(records);

      if (insertError) {
        throw new Error(`Failed to insert preferred users: ${insertError.message}`);
      }
    }
  },

  async syncClientLocations(clientId: string, clientLocations: { location: { id: string } }[]): Promise<void> {
    // Delete all existing locations for this client
    const { error: deleteError } = await supabase.schema("link").from("clients_locations").delete().eq("id", clientId);

    if (deleteError) {
      throw new Error(`Failed to delete existing client locations: ${deleteError.message}`);
    }

    // Insert new client locations
    if (clientLocations.length > 0) {
      const records = clientLocations.map((cl) => ({
        id: clientId, // client_id
        location_id: cl.location.id,
      }));

      const { error: insertError } = await supabase.schema("link").from("clients_locations").insert(records);

      if (insertError) {
        throw new Error(`Failed to insert client locations: ${insertError.message}`);
      }
    }
  },

  async syncClientAbilities(clientId: string, abilities: { ability: { id: string } }[]): Promise<void> {
    // Delete all existing client abilities
    const { error: deleteError } = await supabase.schema("link").from("client_abilities").delete().eq("id", clientId);

    if (deleteError) {
      throw new Error(`Failed to delete existing client abilities: ${deleteError.message}`);
    }

    // Insert new abilities
    if (abilities.length > 0) {
      const records = abilities.map((a) => ({
        id: clientId, // client_id
        ability_id: a.ability.id,
      }));

      const { error: insertError } = await supabase.schema("link").from("client_abilities").insert(records);

      if (insertError) {
        throw new Error(`Failed to insert client abilities: ${insertError.message}`);
      }
    }
  },

  async syncClientRestrictions(clientId: string, restrictions: { restriction: { id: string } }[]): Promise<void> {
    // Delete all existing client restrictions
    const { error: deleteError } = await supabase
      .schema("link")
      .from("client_restrictions")
      .delete()
      .eq("id", clientId);

    if (deleteError) {
      throw new Error(`Failed to delete existing client restrictions: ${deleteError.message}`);
    }

    // Insert new restrictions
    if (restrictions.length > 0) {
      const records = restrictions.map((r) => ({
        id: clientId, // client_id
        restriction_id: r.restriction.id,
      }));

      const { error: insertError } = await supabase.schema("link").from("client_restrictions").insert(records);

      if (insertError) {
        throw new Error(`Failed to insert client restrictions: ${insertError.message}`);
      }
    }
  },

  async getBannedUserIds(clientId: string): Promise<string[]> {
    const { data, error } = await supabase
      .schema("link")
      .from("clients_banned_users")
      .select("user_id")
      .eq("id", clientId);

    if (error) {
      throw new Error(error.message);
    }

    return data?.map((d) => d.user_id) || [];
  },

  async syncClientContacts(clientId: string, contacts: { contact: { id: string }; note?: string }[]): Promise<void> {
    // Get tenant_id from the client record
    const { data: clientData, error: clientError } = await supabase
      .from("clients")
      .select("tenant_id")
      .eq("id", clientId)
      .single();

    if (clientError || !clientData) {
      throw new Error(`Failed to get client tenant: ${clientError?.message || "Client not found"}`);
    }

    const tenantId = clientData.tenant_id;

    // Delete all existing contact links for this client
    const { error: deleteError } = await supabase
      .schema("link")
      .from("clients_contacts")
      .delete()
      .eq("id", clientId)
      .eq("tenant_id", tenantId);

    if (deleteError) {
      throw new Error(`Failed to delete existing client contacts: ${deleteError.message}`);
    }

    // Insert new contact links
    if (contacts.length > 0) {
      const records = contacts.map((c) => ({
        id: clientId, // Client ID
        contact_id: c.contact.id, // Contact ID
        tenant_id: tenantId, // Tenant ID
        note: c.note || null, // Optional note
      }));

      const { error: insertError } = await supabase.schema("link").from("clients_contacts").insert(records);

      if (insertError) {
        throw new Error(`Failed to insert client contacts: ${insertError.message}`);
      }
    }
  },
};
